<template>
  <div class="subscribe-container">
    <div class="subscribe-header">
      <div class="header-top">
        <div class="header-left"></div>
        <h2 class="subscribe-title">订阅我们</h2>
        <div v-if="!loadingMemberInfo && memberInfo" class="member-status-inline">
          <span class="status-text">{{ memberInfo.expire_time ? `订阅到期时间: ${memberInfo.expire_time}` : '未订阅' }}</span>
        </div>
        <div v-else class="member-status-inline-placeholder"></div>
      </div>
      <p class="subscribe-desc">请选择套餐并使用支付宝扫码支付</p>
    </div>
    
    <div class="package-wrapper">
      <div 
        class="package-card" 
        :class="{ 'selected': selectedPackage === 'monthly', 'loading': loadingMonthly }"
        @click="handleSelectPackage('monthly')"
      >
        <div class="package-info">
          <h3 class="package-name">月卡</h3>
          <p class="package-price">¥39.9<span class="price-unit">/月</span></p>
          <div class="package-features">
            <div class="feature-item">
              <i class="el-icon-check"></i>
              <span>30天有效</span>
            </div>
            <div class="feature-item">
              <i class="el-icon-check"></i>
              <span>无限次使用</span>
            </div>
          </div>
        </div>
        <div v-if="loadingMonthly" class="loading-overlay">
          <i class="el-icon-loading"></i>
        </div>
      </div>

      <div 
        class="package-card" 
        :class="{ 'selected': selectedPackage === 'quarterly', 'loading': loadingQuarterly }"
        @click="handleSelectPackage('quarterly')"
      >
        <div class="package-info">
          <h3 class="package-name">季卡</h3>
          <p class="package-price">¥99.9<span class="price-unit">/3个月</span></p>
          <div class="package-features">
            <div class="feature-item">
              <i class="el-icon-check"></i>
              <span>90天有效</span>
            </div>
            <div class="feature-item">
              <i class="el-icon-check"></i>
              <span>无限次使用</span>
            </div>
            <div class="feature-item highlight">
              <i class="el-icon-star-on"></i>
              <span>更优惠</span>
            </div>
          </div>
        </div>
        <div v-if="loadingQuarterly" class="loading-overlay">
          <i class="el-icon-loading"></i>
        </div>
      </div>
    </div>

    <!-- 支付二维码弹窗 -->
    <el-dialog
      :title="paymentDialog.title"
      :visible.sync="paymentDialogVisible"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :append-to-body="true"
      :modal="true"
      width="450px"
      @close="handleClosePaymentDialog"
    >
      <div class="payment-content">
        <div v-if="paymentDialog.loading" class="payment-loading">
          <i class="el-icon-loading"></i>
          <p>正在生成支付二维码...</p>
        </div>

        <div v-else-if="paymentDialog.qrCode" class="qr-code-section">
          <div class="qr-code-wrapper">
            <img 
              :src="paymentDialog.qrCode" 
              alt="支付二维码" 
              class="qr-code-image"
              @error="handleQRCodeError"
              @load="handleQRCodeLoad"
            />
          </div>
          <p class="qr-code-tip">
            <i class="el-icon-info"></i>
            请使用支付宝扫一扫完成支付
          </p>
          <div class="payment-info">
            <div class="info-item">
              <span class="info-label">订单号：</span>
              <span class="info-value">{{ paymentDialog.order_no || '--' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">支付金额：</span>
              <span class="info-value price">¥{{ paymentDialog.amount || '0.00' }}</span>
            </div>
          </div>
        </div>

        <div v-else-if="paymentDialog.error" class="payment-error">
          <i class="el-icon-warning"></i>
          <p>{{ paymentDialog.error }}</p>
          <el-button type="primary" @click="retryGenerateQRCode">重试</el-button>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="handleClosePaymentDialog">取消支付</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { 
  alipayPrecreateMonthly, 
  alipayPrecreateQuarterly, 
  getOrderDetail 
} from '@/api/payment'
import { getMemberInfo } from '@/api/user'
import QRCode from 'qrcode'

export default {
  name: 'Subscribe',
  data() {
    return {
      memberInfo: null, // 会员信息
      loadingMemberInfo: false, // 加载会员信息中
      selectedPackage: null, // 'monthly' | 'quarterly'
      loadingMonthly: false,
      loadingQuarterly: false,
      paymentDialogVisible: false,
      paymentDialog: {
        title: '支付宝支付',
        loading: false,
        qrCode: null,
        order_no: null,
        amount: null,
        error: null,
        packageType: null // 'monthly' | 'quarterly'
      },
      pollTimer: null, // 轮询定时器
      pollCount: 0, // 轮询次数
      maxPollCount: 120, // 最大轮询次数（2分钟，每次1秒）
      paymentSuccessShown: false, // 支付成功提示是否已显示
      payStartTime: null // 支付开始时间（用于计算决策耗时）
    }
  },
  created() {
    this.fetchMemberInfo()
  },
  beforeDestroy() {
    this.clearPollTimer()
  },
  methods: {
    /**
     * 获取会员信息（订阅状态）
     */
    async fetchMemberInfo() {
      this.loadingMemberInfo = true
      try {
        const res = await getMemberInfo()
        if (res && res.data && res.data.code === 1) {
          this.memberInfo = res.data.data || {}
        }
      } catch (e) {
        console.error('获取会员信息失败:', e)
      } finally {
        this.loadingMemberInfo = false
      }
    },
    /**
     * 选择套餐
     */
    async handleSelectPackage(packageType) {
      if (this.loadingMonthly || this.loadingQuarterly) {
        return
      }

      this.selectedPackage = packageType
      
      // 记录支付开始时间（用于计算决策耗时）
      this.payStartTime = Date.now()
      
      // 重置弹窗状态
      this.paymentDialog.packageType = packageType
      this.paymentDialog.loading = true
      this.paymentDialog.qrCode = null
      this.paymentDialog.error = null
      this.paymentDialog.order_no = null
      this.paymentDialog.amount = packageType === 'monthly' ? '39.9' : '99.9'
      
      // 先打开弹窗
      this.paymentDialogVisible = true
      
      await this.$nextTick()
      await this.generateQRCode(packageType)
    },

    /**
     * 生成支付二维码
     */
    async generateQRCode(packageType) {
      try {
        this.paymentDialog.loading = true
        this.paymentDialog.error = null

        // 根据套餐类型调用不同的接口
        const response = packageType === 'monthly' 
          ? await alipayPrecreateMonthly()
          : await alipayPrecreateQuarterly()

        if (response && response.data && response.data.code === 1) {
          const data = response.data.data || {}
          
          
          // 获取二维码URL和订单ID（尝试多种可能的字段名）
          let qrCodeUrl = data.qr_code || data.qrcode || data.qrCode
          this.paymentDialog.order_no = data.order_id || data.order_no
          
          if (qrCodeUrl && typeof qrCodeUrl === 'string') {
            // 检查是否是支付宝二维码链接
            if (qrCodeUrl.includes('qr.alipay.com')) {
              console.log('检测到支付宝二维码链接:', qrCodeUrl)
              // 使用本地二维码生成库转换为base64图片（更稳定，不依赖外部API）
              try {
                const qrCodeDataUrl = await QRCode.toDataURL(qrCodeUrl, {
                  width: 300,
                  margin: 2,
                  color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                  }
                })
                qrCodeUrl = qrCodeDataUrl
                console.log('使用本地库生成二维码成功')
              } catch (error) {
                console.error('生成二维码失败:', error)
                // 如果本地生成失败，fallback到在线API
                qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCodeUrl)}`
                console.log('fallback到在线API:', qrCodeUrl)
              }
            }
          }
          
          this.paymentDialog.qrCode = qrCodeUrl
          this.paymentDialog.loading = false

          if (!this.paymentDialog.qrCode) {
            throw new Error('未获取到支付二维码，请查看控制台日志')
          }

          // 开始轮询订单状态
          this.startPollingOrderStatus(this.paymentDialog.order_no)
        } else {
          throw new Error(response?.data?.msg || '生成支付二维码失败')
        }
      } catch (error) {
        console.error('生成支付二维码失败:', error)
        this.paymentDialog.loading = false
        this.paymentDialog.error = error?.response?.data?.msg || error?.message || '生成支付二维码失败，请稍后重试'
        this.clearPollTimer()
        
        // 埋点：AI面试付费转化（支付失败）
        const userInfo = localStorage.getItem('userInfo')
        const userId = userInfo ? JSON.parse(userInfo).id : ''
        const interviewId = this.$store?.state?.interviewState?.interviewId || ''
        const currentJob = this.$store?.state?.interviewState?.currentInterviewJob
        const positionId = currentJob?.id || currentJob?.job_id || ''
        const payAmount = Math.round(parseFloat(this.paymentDialog.amount || 0) * 100) // 转换为分（整数）
        const payDuration = this.payStartTime ? Date.now() - this.payStartTime : 0
        
        if (this.$tracking && userId && interviewId && positionId) {
          this.$tracking.trackAiInterviewPay(userId, {
            interviewId: interviewId,
            positionId: positionId,
            payEntrance: 'pop_up',
            payStatus: 'fail',
            payAmount: payAmount,
            payMethod: 'alipay',
            failReason: 'system_error',
            payDuration: payDuration
          })
        }
      }
    },

    /**
     * 开始轮询订单状态
     */
    startPollingOrderStatus(order_no) {
      this.clearPollTimer()
      this.pollCount = 0
      this.paymentSuccessShown = false // 重置支付成功标志

      this.pollTimer = setInterval(async () => {
        this.pollCount++

        if (this.pollCount > this.maxPollCount) {
          // 超过最大轮询次数，停止轮询
          this.clearPollTimer()
          this.$message.warning('支付超时，请重新发起支付')
          return
        }

        try {
          const response = await getOrderDetail( order_no)
          
          if (response && response.data && response.data.code === 1) {
            const orderData = response.data.data || {}
            const status = orderData.status

            // 根据接口文档：0-待支付 1-已支付 2-已关闭
            if (status === 1) {
              // 支付成功（确保只显示一次提示）
              if (!this.paymentSuccessShown) {
                this.paymentSuccessShown = true
                this.clearPollTimer()
                
                // 埋点：AI面试付费转化（支付成功）
                const userInfo = localStorage.getItem('userInfo')
                const userId = userInfo ? JSON.parse(userInfo).id : ''
                const interviewId = this.$store?.state?.interviewState?.interviewId || ''
                const currentJob = this.$store?.state?.interviewState?.currentInterviewJob
                const positionId = currentJob?.id || currentJob?.job_id || ''
                const payAmount = Math.round(parseFloat(this.paymentDialog.amount || 0) * 100) // 转换为分（整数）
                const payDuration = this.payStartTime ? Date.now() - this.payStartTime : 0
                
                if (this.$tracking && userId && interviewId && positionId) {
                  this.$tracking.trackAiInterviewPay(userId, {
                    interviewId: interviewId,
                    positionId: positionId,
                    payEntrance: 'pop_up', // 从弹窗提示进入
                    payStatus: 'success',
                    payAmount: payAmount,
                    payMethod: 'alipay',
                    payDuration: payDuration
                  })
                }
                
                this.$message.success('支付成功！')
                
                // 关闭支付弹窗
                setTimeout(() => {
                  this.handleClosePaymentDialog()
                  // 刷新会员信息
                  this.fetchMemberInfo()
                }, 1000)
              } else {
                // 如果已经显示过提示，直接停止轮询（避免重复提示）
                this.clearPollTimer()
              }
            } else if (status === 2) {
              // 订单已关闭
              this.clearPollTimer()
              
              // 埋点：AI面试付费转化（支付失败/取消）
              const userInfo = localStorage.getItem('userInfo')
              const userId = userInfo ? JSON.parse(userInfo).id : ''
              const interviewId = this.$store?.state?.interviewState?.interviewId || ''
              const currentJob = this.$store?.state?.interviewState?.currentInterviewJob
              const positionId = currentJob?.id || currentJob?.job_id || ''
              const payAmount = Math.round(parseFloat(this.paymentDialog.amount || 0) * 100) // 转换为分（整数）
              const payDuration = this.payStartTime ? Date.now() - this.payStartTime : 0
              
              if (this.$tracking && userId && interviewId && positionId) {
                this.$tracking.trackAiInterviewPay(userId, {
                  interviewId: interviewId,
                  positionId: positionId,
                  payEntrance: 'pop_up',
                  payStatus: 'cancel',
                  payAmount: payAmount,
                  payMethod: 'alipay',
                  failReason: 'payment_timeout',
                  payDuration: payDuration
                })
              }
              
              this.paymentDialog.error = '订单已关闭，请重新发起支付'
              this.$message.warning('订单已关闭，请重新发起支付')
            }
          }
        } catch (error) {
          console.error('查询订单状态失败:', error)
          // 查询失败不影响轮询，继续下次查询
        }
      }, 1000) // 每秒轮询一次
    },

    /**
     * 清除轮询定时器
     */
    clearPollTimer() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      this.pollCount = 0
    },

    /**
     * 重试生成二维码
     */
    retryGenerateQRCode() {
      if (this.paymentDialog.packageType) {
        this.generateQRCode(this.paymentDialog.packageType)
      }
    },

    /**
     * 关闭支付弹窗
     */
    handleClosePaymentDialog() {
      // 如果支付未成功且用户主动关闭，记录取消埋点
      if (!this.paymentSuccessShown && this.payStartTime && this.paymentDialog.order_no) {
        const userInfo = localStorage.getItem('userInfo')
        const userId = userInfo ? JSON.parse(userInfo).id : ''
        const interviewId = this.$store?.state?.interviewState?.interviewId || ''
        const currentJob = this.$store?.state?.interviewState?.currentInterviewJob
        const positionId = currentJob?.id || currentJob?.job_id || ''
        const payAmount = Math.round(parseFloat(this.paymentDialog.amount || 0) * 100) // 转换为分（整数）
        const payDuration = Date.now() - this.payStartTime
        
        if (this.$tracking && userId && interviewId && positionId) {
          this.$tracking.trackAiInterviewPay(userId, {
            interviewId: interviewId,
            positionId: positionId,
            payEntrance: 'pop_up',
            payStatus: 'cancel',
            payAmount: payAmount,
            payMethod: 'alipay',
            failReason: 'user_cancel',
            payDuration: payDuration
          })
        }
      }
      
      this.clearPollTimer()
      this.paymentSuccessShown = false // 关闭弹窗时重置标志
      this.payStartTime = null // 重置支付开始时间
      this.paymentDialogVisible = false
      this.paymentDialog.qrCode = null
      this.paymentDialog.order_no = null
      this.paymentDialog.error = null
      this.paymentDialog.packageType = null
      this.selectedPackage = null
      this.loadingMonthly = false
      this.loadingQuarterly = false
    },

    /**
     * 格式化到期时间
     */
    formatExpireTime(time) {
      if (!time) return '--'
      try {
        const date = new Date(time)
        if (isNaN(date.getTime())) return time
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}/${month}/${day} ${hours}:${minutes}`
      } catch (e) {
        return time
      }
    },

    /**
     * 二维码加载成功
     */
    handleQRCodeLoad() {
      console.log('二维码图片加载成功')
    },

    /**
     * 二维码加载失败
     */
    handleQRCodeError(e) {
      console.error('二维码图片加载失败:', e)
      console.error('二维码URL:', this.paymentDialog.qrCode)
      this.$message.error('二维码加载失败，请重试')
    }
  }
}
</script>

<style scoped lang="scss">
.subscribe-container {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.subscribe-header {
  text-align: center;
  margin-bottom: 48px;
}

.header-top {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.header-left {
  flex: 1;
}

.subscribe-title {
  font-size: 28px;
  font-weight: bold;
  color: #1f2937;
  margin: 0;
  background: linear-gradient(90deg, #62AFFF 0%, #9C41FF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  flex-shrink: 0;
}

/* 会员状态显示（内联） */
.member-status-inline {
  flex: 1;
  text-align: right;
  flex-shrink: 0;
  
  .status-text {
    font-size: 16px;
    color: #6b7280;
    font-weight: 400;
  }
}

.member-status-inline-placeholder {
  flex: 1;
  flex-shrink: 0;
}

.subscribe-desc {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.package-wrapper {
  display: flex;
  justify-content: center;
  gap: 32px;
  flex-wrap: wrap;
  margin-top: 40px;
}

.package-card {
  position: relative;
  width: 320px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  padding: 32px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    border-color: #62AFFF;
    box-shadow: 0 8px 24px rgba(98, 175, 255, 0.2);
    transform: translateY(-4px);
  }

  &.selected {
    border-color: #9C41FF;
    box-shadow: 0 8px 24px rgba(156, 65, 255, 0.3);
    background: linear-gradient(135deg, rgba(156, 65, 255, 0.05) 0%, rgba(98, 175, 255, 0.05) 100%);
  }

  &.loading {
    pointer-events: none;
    opacity: 0.7;
  }
}

.package-info {
  text-align: center;
}

.package-name {
  font-size: 24px;
  font-weight: bold;
  color: #1f2937;
  margin: 0 0 16px 0;
}

.package-price {
  font-size: 36px;
  font-weight: bold;
  color: #9C41FF;
  margin: 0 0 24px 0;

  .price-unit {
    font-size: 18px;
    color: #6b7280;
    font-weight: normal;
  }
}

.package-features {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
}

.feature-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  color: #4b5563;

  i {
    color: #10b981;
    font-size: 16px;
  }

  &.highlight {
    color: #9C41FF;
    font-weight: 500;

    i {
      color: #fbbf24;
    }
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  i {
    font-size: 32px;
    color: #62AFFF;
  }
}

/* 支付弹窗样式 */
.payment-content {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.payment-loading {
  text-align: center;
  padding: 60px 20px;

  i {
    font-size: 48px;
    color: #62AFFF;
    margin-bottom: 16px;
  }

  p {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
  }
}

.qr-code-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-code-wrapper {
  width: 260px;
  height: 260px;
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-code-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.qr-code-tip {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    color: #3b82f6;
  }
}

.payment-info {
  width: 100%;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;

  .info-label {
    color: #6b7280;
  }

  .info-value {
    color: #1f2937;
    font-weight: 500;

    &.price {
      color: #9C41FF;
      font-size: 18px;
      font-weight: bold;
    }
  }
}

.payment-error {
  text-align: center;
  padding: 60px 20px;

  i {
    font-size: 48px;
    color: #ef4444;
    margin-bottom: 16px;
  }

  p {
    font-size: 16px;
    color: #6b7280;
    margin: 0 0 24px 0;
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .package-wrapper {
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }

  .package-card {
    width: 100%;
    max-width: 320px;
  }

  .subscribe-title {
    font-size: 24px;
  }
}
</style>
